import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * StarRating — display + input control for 1..5 star ratings.
 *
 * In display mode (`readOnly`), partial-star filling is supported so the
 * book detail badge can render a precise "4.3 average" with the trailing
 * star painted 30% full. In input mode, hovering a star previews the
 * value via a local hoverIndex state; clicking commits via `onChange`.
 * Empty (rating = 0) is the "not yet rated" state — the user can't
 * deselect a star once chosen because the underlying review row carries
 * a NOT NULL rating; if they want to remove their review they hit the
 * delete button on the form instead.
 *
 * Sizing is opinionated: `sm` is for inline badges in book cards, `md`
 * for the per-review row, `lg` for the input form. Width auto-fits five
 * stars with no extra layout work needed at the call site.
 */
export interface StarRatingProps {
  value: number;
  onChange?: (next: number) => void;
  size?: 'sm' | 'md' | 'lg';
  readOnly?: boolean;
  className?: string;
  /** Optional aria-label override; defaults to "Star rating". */
  label?: string;
}

const SIZE_CLASSES: Record<NonNullable<StarRatingProps['size']>, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-7 w-7',
};

export function StarRating({
  value,
  onChange,
  size = 'md',
  readOnly = false,
  className,
  label,
}: StarRatingProps) {
  const [hover, setHover] = useState<number | null>(null);
  const sizeClass = SIZE_CLASSES[size];
  const displayValue = hover ?? value;

  // Display mode — render five overlaid stars with the active value
  // expressed as a clipped fill on the trailing partial star.
  if (readOnly || !onChange) {
    return (
      <div
        className={cn('inline-flex items-center gap-0.5', className)}
        role="img"
        aria-label={label ?? `Rated ${value.toFixed(1)} of 5`}
      >
        {[1, 2, 3, 4, 5].map((position) => {
          // Per-star fill amount: full for stars below the value, empty
          // above, and clipped between for the trailing fractional star.
          const fill = Math.max(0, Math.min(1, value - (position - 1)));
          return (
            <div key={position} className="relative">
              <Star className={cn(sizeClass, 'text-or-slate-200')} aria-hidden />
              {fill > 0 ? (
                <div
                  className="pointer-events-none absolute inset-0 overflow-hidden"
                  style={{ width: `${fill * 100}%` }}
                >
                  <Star
                    className={cn(sizeClass, 'fill-or-orange-500 text-or-orange-500')}
                    aria-hidden
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  // Input mode — five buttons, each updating the rating on click. We
  // bind hover/focus to the same preview state so keyboard users see
  // the same affordance as mouse users.
  return (
    <div
      className={cn('inline-flex items-center gap-1', className)}
      role="radiogroup"
      aria-label={label ?? 'Star rating'}
      onMouseLeave={() => setHover(null)}
    >
      {[1, 2, 3, 4, 5].map((position) => {
        const active = position <= displayValue;
        return (
          <button
            key={position}
            type="button"
            role="radio"
            aria-checked={value === position}
            aria-label={`${position} star${position === 1 ? '' : 's'}`}
            tabIndex={value === position || (value === 0 && position === 1) ? 0 : -1}
            onClick={() => onChange(position)}
            onMouseEnter={() => setHover(position)}
            onFocus={() => setHover(position)}
            onBlur={() => setHover(null)}
            className="rounded-full p-1 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
          >
            <Star
              className={cn(
                sizeClass,
                active
                  ? 'fill-or-orange-500 text-or-orange-500'
                  : 'text-or-slate-300 hover:text-or-orange-300',
              )}
              aria-hidden
            />
          </button>
        );
      })}
    </div>
  );
}
